import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import { Link } from 'react-router-dom'

import useDeferredCaptchaSubmit from 'hooks/useDeferredCaptchaSubmit'
import {
  dispatchLogin,
  dispatchRequestAccountCode,
  dispatchVerifyOtp
} from 'reduxModules/auth/dispatchers'
import { loginBoxSelector } from 'reduxModules/app/selectors'
import keyUpHandler from '../../utils/keyUpHandler'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import EMAIL_PATTERN from '../../utils/emailPattern'
import InlineError, { ariaErrorProps } from '../InlineError'
import NewsletterConsent from '../NewsletterConsent'
import OtpCodeStep from '../OtpCodeStep'
import SocialButtons from '../SocialButtons'
import { AuthFooter, AuthTitle, OrDivider, TermsLine } from '../AuthParts'
import '../RenderedField/styles.css'
import './styles.css'

const TITLES = {
  email: 'Log in',
  password: 'Welcome back',
  create: 'Create your account'
}

/**
 * SPEC-101 D6/D9: the login page, email first, then branch.
 *
 *   email    — email + Continue (purpose "login", no code forced), with the
 *              social buttons and the school/library link below.
 *   password — the account has a password: the password field, with
 *              "Email me a code instead" (the same request with send_code).
 *   create   — no account for that email, and nothing has been created yet
 *              (D9). The person confirms with the newsletter box and the
 *              terms; Create account then creates it at once (D10) and they
 *              are signed in.
 *   code     — the shared code step, for logins only (an account without a
 *              password goes straight here).
 *
 * Password managers: the email, password and create steps are ONE form, and
 * the email input (autocomplete="username") stays in it, read-only, when the
 * password field (autocomplete="current-password") appears, so a manager can
 * pair them and offer to save after a normal submit.
 */
export function LoginBox({
  autoFocus = false,
  initialEmail = '',
  location,
  isMobile,
  recaptcha,
  requestCode,
  verifyCode,
  passwordLogin
}) {
  const [step, setStep] = useState('email')
  const [passwordVisible, setPasswordVisible] = useState(false)
  // How to resend the last code
  const lastRequest = useRef(null)
  const [sendCount, setSendCount] = useState(0)

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: initialEmail,
      password: '',
      marketingConsent: false
    }
  })
  const email = watch('email') || ''
  const emailOk = EMAIL_PATTERN.test(email)
  const marketingConsent = Boolean(watch('marketingConsent'))

  const toCodeStep = (request) => {
    lastRequest.current = request
    setSendCount((count) => count + 1)
    setStep('code')
  }

  // Every request goes through one Turnstile-gated handler; `action` says
  // which. Each request consumes the token, and the (invisible) widget
  // re-issues one for the next.
  const actions = {
    continue: ({ email: address }) =>
      requestCode({ email: address, purpose: 'login', sendCode: false }).then(
        (result) => {
          if (!result.flow) return
          if (result.sent)
            toCodeStep({ action: 'code-instead', email: address })
          else if (result.flow === 'login') setStep('password')
          else setStep('create')
        }
      ),
    password: ({ email: address, password }) =>
      passwordLogin({ email: address, password }),
    'code-instead': ({ email: address }) =>
      requestCode({ email: address, purpose: 'login', sendCode: true }).then(
        (result) => {
          if (result.sent)
            toCodeStep({ action: 'code-instead', email: address })
        }
      ),
    create: ({ email: address, marketingConsent: consent }) =>
      requestCode({
        email: address,
        purpose: 'signup',
        sendCode: true,
        marketingConsent: Boolean(consent)
      }).then((result) => {
        // D10: created and signed in (the dispatcher carries on from here);
        // an account that turned up in between gets a login code instead
        if (result.sent) toCodeStep({ action: 'code-instead', email: address })
      })
  }
  const { submit, awaitingVerification, isSubmitting } =
    useDeferredCaptchaSubmit(recaptcha.token, (data) =>
      actions[data.action](data)
    )
  const busy = isSubmitting || awaitingVerification

  const onSubmit = handleSubmit((data) =>
    submit({ ...data, action: step === 'email' ? 'continue' : step })
  )

  const changeEmail = () => setStep('email')

  const verify = (address, code) =>
    verifyCode(
      { email: address, code },
      { flow: 'login', trigger: 'login', location, isMobile }
    )

  if (step === 'code') {
    const sentTo = getValues('email')
    return (
      <div id="LoginBox" role="main">
        <div className="sign-in-box auth-form-box">
          <OtpCodeStep
            sentTo={sentTo}
            sendCount={sendCount}
            title="Welcome back"
            subtitle={
              <>
                Enter the 6-digit code we sent to <strong>{sentTo}</strong> to
                log in.
              </>
            }
            submitLabel="Log in"
            verifyCode={verify}
            onResend={() => submit(lastRequest.current)}
            onBack={changeEmail}
            sending={busy}
            canResend={Boolean(recaptcha.token)}
            captchaAppearance="interaction-only"
          />
        </div>
      </div>
    )
  }

  const submitLabel = {
    email: 'Continue',
    password: 'Log in',
    create: 'Create account'
  }[step]
  const submitDisabled =
    busy || !emailOk || (step === 'password' && !watch('password'))

  return (
    <div id="LoginBox" role="main">
      <div className="sign-in-box auth-form-box">
        <AuthTitle>{TITLES[step]}</AuthTitle>
        {step === 'create' && (
          <p className="LoginBox__note">
            There&apos;s no account for <strong>{email}</strong> yet. Create
            one?
          </p>
        )}
        <form id="SignInForm" onSubmit={onSubmit} noValidate>
          <div
            className={classnames('field', '-floating', {
              '-error': errors.email,
              '-locked': step !== 'email'
            })}
          >
            <div className="field-container">
              <input
                {...register('email', {
                  required: 'This field is required.',
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: 'Invalid email address.'
                  }
                })}
                id="login-email"
                type="email"
                autoComplete="username"
                placeholder=" "
                readOnly={step !== 'email'}
                autoFocus={autoFocus && step === 'email'}
                {...ariaErrorProps('login-email', errors.email)}
              />
              <label htmlFor="login-email">Email</label>
              <InlineError id="login-email" message={errors.email?.message} />
            </div>
          </div>
          {step !== 'email' && (
            <button
              type="button"
              className="LoginBox__change"
              onClick={changeEmail}
            >
              Change email
            </button>
          )}

          {step === 'password' && (
            <div className="field -floating">
              <div className="field-container">
                <input
                  {...register('password')}
                  id="login-password"
                  type={passwordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder=" "
                  autoFocus
                />
                <label htmlFor="login-password">Password</label>
                <i
                  className={classnames(
                    'icon',
                    passwordVisible
                      ? 'icon-passcodewatch'
                      : 'icon-passcodewatch disabled'
                  )}
                  aria-label="Toggle password visibility"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  onKeyUp={(event) =>
                    keyUpHandler(event, () =>
                      setPasswordVisible(!passwordVisible)
                    )
                  }
                  role="button"
                  tabIndex="0"
                />
              </div>
            </div>
          )}

          {step === 'create' && (
            <>
              <NewsletterConsent
                id="login-newsletter"
                inputProps={register('marketingConsent')}
                privacyLink={false}
              />
              <TermsLine />
            </>
          )}

          {/* Invisible unless Cloudflare wants an interaction */}
          <CaptchaStandalone appearance="interaction-only" />
          <button
            type="submit"
            className="btn -wide -orange"
            disabled={submitDisabled}
            style={{ width: '100%' }}
          >
            {busy ? 'Please wait…' : submitLabel}
          </button>
        </form>

        {step === 'password' && (
          <div className="LoginBox__alternatives">
            <button
              type="button"
              className="LoginBox__link"
              disabled={busy}
              onClick={() =>
                submit({ action: 'code-instead', email: getValues('email') })
              }
            >
              Email me a code instead
            </button>
            <Link to="/users/password/new">Forgot password?</Link>
          </div>
        )}

        {step === 'email' && (
          <>
            <OrDivider />
            <SocialButtons
              location={location}
              isMobile={isMobile}
              showOpenAthens
            />
            <AuthFooter>
              New here? <Link to="/users/signup">Create an account</Link>
            </AuthFooter>
          </>
        )}
      </div>
    </div>
  )
}

LoginBox.propTypes = {
  autoFocus: PropTypes.bool,
  initialEmail: PropTypes.string,
  location: PropTypes.object,
  isMobile: PropTypes.bool,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  }),
  requestCode: PropTypes.func.isRequired,
  verifyCode: PropTypes.func.isRequired,
  passwordLogin: PropTypes.func.isRequired
}

export default connect(
  loginBoxSelector,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const { dispatch } = dispatchProps
    return {
      ...stateProps,
      ...ownProps,
      requestCode: (request) =>
        dispatchRequestAccountCode(
          dispatch,
          { ...request, recaptcha: stateProps.recaptcha },
          ownProps
        ),
      verifyCode: (data, props) =>
        dispatchVerifyOtp(dispatch, data, { ...ownProps, ...props }),
      passwordLogin: (data) =>
        dispatchLogin(
          dispatch,
          { ...data, recaptcha: stateProps.recaptcha },
          ownProps
        )
    }
  }
)(LoginBox)
